/**
 * File: Hero.jsx
 * Path: /src/components/home/Hero.jsx
 * Description: Hero section for the homepage with animated text and call-to-action buttons.
 * Key Features: Typed text animation, floating decorative elements, and gradient background.
 * Maintenance Notes: Main attention-grabber; update messaging and CTAs as marketing focus changes.
 */

import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { TypeAnimation } from 'react-type-animation';
import Button from '../common/Button';

const Hero = () => {
  useEffect(() => {
    // Add mesh gradient background to body when hero mounts
    document.body.classList.add('mesh-gradient');
    
    // Clean up when unmounts
    return () => {
      document.body.classList.remove('mesh-gradient');
    };
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut",
      },
    },
  };

  const floatingObjectVariants = {
    float: {
      y: [0, -15, 0],
      transition: {
        duration: 3,
        repeat: Infinity,
        repeatType: "reverse",
        ease: "easeInOut",
      },
    },
  };

  return (
    <section className="relative pt-20 pb-24 md:pt-32 md:pb-32 overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute right-0 top-20 w-72 h-72 bg-accent-300/20 rounded-full filter blur-3xl"></div>
        <div className="absolute left-10 bottom-10 w-60 h-60 bg-primary-400/20 rounded-full filter blur-3xl"></div>
        <div className="absolute left-1/3 top-1/4 w-40 h-40 bg-secondary-300/20 rounded-full filter blur-3xl"></div>
        
        {/* Floating shapes */}
        <motion.div
          className="absolute right-1/4 top-1/5 hidden md:block"
          variants={floatingObjectVariants}
          animate="float"
        >
          <div className="w-16 h-16 border-2 border-primary-500/30 rounded-lg rotate-12"></div>
        </motion.div>
        
        <motion.div
          className="absolute left-1/4 bottom-1/4 hidden md:block"
          variants={floatingObjectVariants}
          animate="float"
          transition={{ delay: 1 }}
        >
          <div className="w-12 h-12 border-2 border-accent-500/30 rounded-full"></div>
        </motion.div>
        
        <motion.div
          className="absolute right-1/5 bottom-1/3 hidden md:block"
          variants={floatingObjectVariants}
          animate="float"
          transition={{ delay: 0.5 }}
        >
          <div className="w-10 h-10 border-2 border-secondary-500/30 rotate-45"></div>
        </motion.div>
      </div>

      <div className="container-padding mx-auto relative z-10">
        <motion.div
          className="text-center max-w-4xl mx-auto"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Subtitle */}
          <motion.div variants={itemVariants} className="mb-6">
            <span className="inline-block px-4 py-2 bg-primary-50 text-primary-600 rounded-full text-sm font-medium">
              Legal AI for the Modern Firm
            </span>
          </motion.div>
          
          {/* Main Title */}
          <motion.h1 
            variants={itemVariants}
            className="text-4xl md:text-6xl font-bold mb-6 leading-tight"
          >
            <span className="block">Transform Your Legal Practice with</span>
            <span className="gradient-text">
              <TypeAnimation
                sequence={[
                  'Smart Research.',
                  1000,
                  'Contract Intelligence.',
                  1000,
                  'Predictive Analytics.',
                  1000,
                  'Veritect.ai.',
                  1000,
                ]}
                speed={40} 
                deletionSpeed={50}
                repeat={Infinity}
                style={{ display: 'inline-block' }}
              />
            </span>
          </motion.h1>
          
          {/* Description */}
          <motion.p 
            variants={itemVariants}
            className="text-lg md:text-xl text-dark-600 mb-8 max-w-3xl mx-auto"
          >
            Harness the power of AI to streamline legal research, analyze contracts with precision, 
            and predict case outcomes with unprecedented accuracy.
          </motion.p>
          
          {/* CTA Buttons */}
          <motion.div 
            variants={itemVariants}
            className="flex flex-col sm:flex-row justify-center gap-4 mb-12"
          >
            <Button 
              variant="primary" 
              size="lg"
              icon={
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              }
            >
              Get Started Now
            </Button>
            
            <Button 
              variant="outline"
              size="lg"
              icon={
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                </svg>
              }
            >
              Watch Demo
            </Button>
          </motion.div>
          
          {/* Trusted By - Commented out as requested */}
          {/*
          <motion.div 
            variants={itemVariants}
            className="pt-8 border-t border-gray-200"
          >
            <p className="text-sm text-dark-400 mb-4">Trusted by leading law firms</p>
            <div className="flex flex-wrap justify-center gap-x-12 gap-y-6">
              {['Morgan & Price', 'LexCorp Legal', 'Justice Partners', 'Global Law Alliance', 'Statute Defenders'].map((company, index) => (
                <div key={index} className="text-dark-300 font-medium text-lg md:text-xl opacity-70">
                  {company}
                </div>
              ))}
            </div>
          </motion.div>
          */}
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;